/**
 * /airport · the Airport Services pillar surface.
 *
 * Biphasic flow:
 *   Phase 1 · landing  → search bar + top airports rail + Ask
 *   Phase 2 · selected → airport hero + 6-service dashboard:
 *
 *        ① Lounges         · Google Places-backed sub-grid
 *        ② Dining & Bars   · Google Places-backed sub-grid
 *        ③ Meet & Greet    · editorial · "Request via Concierge®"
 *        ④ Fast-track      · editorial · "Request via Concierge®"
 *        ⑤ Chauffeur       · editorial · "Request via Concierge®"
 *        ⑥ Other services  · editorial · "Request via Concierge®"
 *
 * Each lounge/dining card opens a small detail drawer mirroring /fit's
 * pattern. Briefs route via Aurelion dispatcher when tenant=aurelion.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useEmbedMember } from "@/hooks/useEmbedMember";
import { TableFooter } from "@/components/table/TableFooter";
import PillarCard from "@/components/shared/PillarCard";
import ConciergeHero from "@/components/global/ConciergeHero";
import ConciergeOnDutyPill from "@/components/global/ConciergeOnDutyPill";
import ConciergeSurfaceMenu from "@/components/global/ConciergeSurfaceMenu";
import CrossSurfaceNav from "@/components/global/CrossSurfaceNav";
import SavedHotelEtaBand from "@/components/shared/SavedHotelEtaBand";
import { DrawerVoiceLine, DrawerReviewsBand } from "@/components/shared/DrawerAtmosphere";

import MemberContextBand from "../components/MemberContextBand";
const API = process.env.REACT_APP_BACKEND_URL || "";

const T = {
  bg:    "#F4EFE6",
  card:  "#FCFAF4",
  ink:   "#211C15",
  ink2:  "#5C5447",
  ink3:  "#8A8071",
  line:  "#E5DCCB",
  line2: "#D8CDB7",
  brass: "#8B6914",
};

// ── Brand assets · founder-curated editorial photos · stored in
//    Cloudinary (cloud `datmqdk2c`, folder `lc/airport-services/`) so
//    the React bundle stays slim and the photos can be swapped without
//    a code deploy. `f_auto,q_auto` lets Cloudinary serve WebP/AVIF
//    automatically with smart quality. Versionless URL → always-latest.
const CLD = "https://res.cloudinary.com/datmqdk2c/image/upload/f_auto,q_auto,w_1200/lc/airport-services";

const SERVICES = [
  { code: "lounge",    label: "Lounges",                fallback_icon: "🛋️", google: true,  cta: "View lounges",       branded_photo: `${CLD}/lounges`    },
  { code: "dining",    label: "Dining & Bars",          fallback_icon: "🍷", google: true,  cta: "View dining",        branded_photo: `${CLD}/dining`     },
  { code: "greet",     label: "Meet & Greet",           fallback_icon: "🤝", google: false, cta: "Arrange greet",      viator_key: "meet_greet", branded_photo: `${CLD}/meet-greet` },
  { code: "fasttrack", label: "Fast-track Immigration", fallback_icon: "⚡", google: false, cta: "Arrange fast-track", viator_key: "fast_track", branded_photo: `${CLD}/fast-track` },
  { code: "chauffeur", label: "Chauffeur Transfer",     fallback_icon: "🚗", google: false, cta: "Book transfer",      viator_key: "chauffeur",  branded_photo: `${CLD}/chauffeur`  },
  { code: "other",     label: "Other Services",         fallback_icon: "✦",  google: false, cta: "Send a brief",       branded_photo: `${CLD}/other`      },
];

const SERVICE_BLURBS = {
  greet:     "A Concierge®-vetted greet team meets you at the gate, escorts through immigration, baggage and on to your transfer. Arranged anywhere our network reaches.",
  fasttrack: "Priority immigration & security · skip the queue at hubs that allow it (BOM/DEL/DXB/SIN/LHR/CDG and more).",
  chauffeur: "Black-car / SUV / Maybach · airport-to-hotel, airport-to-airport, multi-stop · our drivers wait, our cars are tracked.",
  other:     "Lost luggage retrieval · SIM card on landing · unaccompanied minor escorts · special-assistance · pet-friendly handling.",
};


export default function AirportPage() {
  const [q, setQ] = useState("");
  const [topAirports, setTopAirports] = useState([]);
  const [airport, setAirport] = useState(null);     // selected airport (detail)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeService, setActiveService] = useState(null);  // service drawer
  const [activeBrief, setActiveBrief]     = useState(null);  // brief modal: {service, venue?}
  const [activeVenue, setActiveVenue]     = useState(null);  // small venue drawer

  // viewport
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 760px)");
    const update = () => setIsNarrow(mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener("change", update) : mq.addListener(update);
    return () => mq.removeEventListener ? mq.removeEventListener("change", update) : mq.removeListener(update);
  }, []);

  // Load top airports rail
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/airport/top`);
        const j = await r.json();
        if (!cancelled) setTopAirports(j.airports || []);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  async function pickAirportByQuery(query) {
    setLoading(true);
    setError(null);
    setAirport(null);
    setActiveService(null);
    try {
      const r = await fetch(`${API}/api/airport/resolve?q=${encodeURIComponent(query)}`);
      const j = await r.json();
      if (!j.airport) {
        setError(`No airport found for "${query}". Try the IATA code or full name.`);
        return;
      }
      // Now fetch the detail for richer content
      const d = await fetch(`${API}/api/airport/${encodeURIComponent(j.airport.place_id)}`);
      const dj = await d.json();
      setAirport(dj.airport || j.airport);
    } catch (e) {
      setError(e.message || "Could not resolve airport.");
    } finally {
      setLoading(false);
    }
  }

  async function pickAirportByPlaceId(placeId) {
    setLoading(true); setError(null); setAirport(null); setActiveService(null);
    try {
      const r = await fetch(`${API}/api/airport/${encodeURIComponent(placeId)}`);
      const j = await r.json();
      setAirport(j.airport);
    } catch (e) {
      setError(e.message || "Could not load airport.");
    } finally { setLoading(false); }
  }

  function openService(svc) {
    if (svc.google) {
      setActiveService(svc);     // Google sub-grid (lounges/dining)
    } else if (svc.viator_key && airport?.viator_overview?.[svc.viator_key]?.count > 0) {
      // Editorial service with Viator inventory · show book-on-Viator cards
      setActiveService({ ...svc, viator_mode: true });
    } else {
      // Editorial service with no Viator inventory · go straight to brief
      setActiveBrief({ service: svc.code, service_label: svc.label });
    }
  }

  function backToLanding() {
    setAirport(null); setActiveService(null); setQ("");
  }

  return (
    <div data-testid="airport-page-root" data-table-surface="airport" style={{ background: T.bg, minHeight: "100vh", color: T.ink, fontFamily: "'Hanken Grotesk', system-ui, sans-serif", "--t-bg": T.bg, "--t-ink": T.ink, "--t-ink2": T.ink2, "--t-ink3": T.ink3, "--t-line": T.line, "--t-brass": T.brass, "--t-verified": "#2f8a52" }}>
      <AirportTopBar onHome={backToLanding} />
      <MemberContextBand pillar="airport" />

      <main style={{ maxWidth: 1180, margin: "0 auto", padding: "12px 28px 80px" }}>
{!airport && (
          <>
            <ConciergeHero
              testid="airport-hero"
              eyebrow="Concierge® · Airport"
              headline="Which airport"
              headlineTail="needs taking care of?"
              subtitle={<>Lounges, transfers, fast-track, meet &amp; greet&mdash;arranged through your Concierge®.</>}
              maxWidth={980}
            >
              <form
                data-testid="airport-search-bar"
                onSubmit={(e) => { e.preventDefault(); if (q.trim()) pickAirportByQuery(q.trim()); }}
                style={{
                  background: T.card, border: `1px solid ${T.line2}`, borderRadius: 18,
                  padding: isNarrow ? "12px" : "8px 8px 8px 18px",
                  display: "grid",
                  gridTemplateColumns: isNarrow ? "1fr" : "1fr auto",
                  gap: isNarrow ? 10 : 8,
                  alignItems: "center",
                  maxWidth: 720, margin: "0 auto",
                  boxShadow: "0 6px 22px rgba(33,28,21,0.04)",
                }}
              >
                <input
                  data-testid="airport-search-q"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Airport, city or IATA — Mumbai · BOM · Heathrow…"
                  style={{
                    border: 0, background: "transparent", outline: "none",
                    fontSize: 16, color: T.ink, padding: "10px 12px",
                    width: "100%", boxSizing: "border-box", minWidth: 0,
                  }}
                />
                <button
                  type="submit"
                  data-testid="airport-search-submit"
                  disabled={loading || !q.trim()}
                  style={{
                    background: T.ink, color: T.card, border: 0, borderRadius: 14,
                    padding: "12px 22px", fontSize: 14, fontWeight: 600,
                    cursor: loading ? "wait" : (!q.trim() ? "not-allowed" : "pointer"),
                    opacity: (loading || !q.trim()) ? 0.65 : 1,
                    fontFamily: "inherit",
                    width: isNarrow ? "100%" : "auto",
                  }}
                >{loading ? "Finding…" : "Find airport"}</button>
              </form>

              <div style={{ display: "flex", justifyContent: "center", gap: 18, marginTop: 18, flexWrap: "wrap" }}>
                <button
                  type="button"
                  data-testid="airport-hero-ask"
                  onClick={() => setActiveBrief({ service: "other", service_label: "Other airport service" })}
                  style={{
                    background: "transparent", color: T.brass,
                    border: `1px solid ${T.brass}`, borderRadius: 999,
                    padding: "10px 22px", fontSize: 13.5, fontWeight: 500,
                    cursor: "pointer", fontFamily: "inherit",
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = T.brass; e.currentTarget.style.color = T.bg; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.brass; }}
                >
                  Ask the Concierge®
                </button>
              </div>

              {/* G4 · saved-hotel ETA whisper (quiet · only when localStorage has lc_recent_hotel) */}
              <SavedHotelEtaBand
                airportLat={airport?.lat || airport?.location?.lat}
                airportLng={airport?.lng || airport?.location?.lng}
                airportLabel={airport ? (airport.iata || airport.name) : null}
              />

              <p style={{ fontSize: 12.5, color: T.ink3, fontStyle: "italic", marginTop: 14, marginBottom: 0 }}>
                Pick an airport · or ask us to arrange any service worldwide.
              </p>
            </ConciergeHero>

            {/* Top airports rail */}
            {topAirports.length > 0 && (
              <section data-testid="airport-top-rail" style={{ marginTop: 36 }}>
                <div style={{ marginBottom: 14 }}>
                  <span style={{
                    fontFamily: "ui-monospace, monospace",
                    fontSize: 11, letterSpacing: "0.22em",
                    color: T.brass, fontWeight: 600, textTransform: "uppercase",
                  }}>Major Hubs</span>
                  <h2 className="t-serif" style={{ fontSize: 24, fontWeight: 500, color: T.ink, margin: "6px 0 0" }}>
                    Where members fly most
                  </h2>
                </div>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                  gap: 12,
                }}>
                  {topAirports.map((a) => (
                    <button
                      key={a.iata}
                      type="button"
                      data-testid={`airport-top-${a.iata}`}
                      onClick={() => pickAirportByQuery(a.google_query)}
                      style={{
                        background: a.hero_url
                          ? `linear-gradient(180deg, rgba(33,28,21,0.05) 30%, rgba(33,28,21,0.62) 100%), url(${a.hero_url}) center/cover no-repeat`
                          : T.card,
                        border: `1px solid ${a.hero_url ? "transparent" : T.line}`,
                        borderRadius: 14,
                        padding: "16px 14px",
                        textAlign: "left",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        transition: "all 0.15s",
                        color: a.hero_url ? "#fff" : T.ink,
                        minHeight: a.hero_url ? 160 : "auto",
                        display: "flex", flexDirection: "column", justifyContent: "flex-end",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; if (!a.hero_url) e.currentTarget.style.borderColor = T.brass; }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; if (!a.hero_url) e.currentTarget.style.borderColor = T.line; }}
                    >
                      <div style={{
                        fontFamily: "ui-monospace, monospace",
                        fontSize: 22, fontWeight: 700,
                        color: a.hero_url ? "#fff" : T.brass,
                        letterSpacing: "0.06em",
                        textShadow: a.hero_url ? "0 1px 8px rgba(0,0,0,0.45)" : "none",
                      }}>{a.iata}</div>
                      <div className="t-serif" style={{ fontSize: 16, fontWeight: 500, color: a.hero_url ? "#fff" : T.ink, marginTop: 4, textShadow: a.hero_url ? "0 1px 6px rgba(0,0,0,0.40)" : "none" }}>
                        {a.name}
                      </div>
                      <div style={{ fontSize: 11.5, color: a.hero_url ? "rgba(255,255,255,0.85)" : T.ink3, marginTop: 4, lineHeight: 1.45 }}>
                        {a.sub}
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {error && (
              <p data-testid="airport-error" style={{ textAlign: "center", color: "#9a3535", fontSize: 14, marginTop: 24 }}>{error}</p>
            )}
          </>
        )}

        {airport && (
          <>
            {/* G4 live · saved-hotel ETA whisper now that an airport is picked. */}
            <SavedHotelEtaBand
              airportLat={airport?.lat || airport?.location?.lat}
              airportLng={airport?.lng || airport?.location?.lng}
              airportLabel={airport ? (airport.iata || airport.name) : null}
              testid="airport-detail-saved-hotel-eta"
            />
            <AirportDashboard
              airport={airport}
              onBack={backToLanding}
              onOpenService={openService}
            />
          </>
        )}
      </main>

      <TableFooter slug="airport" />

      {activeService && airport && (
        <ServiceSubGridDrawer
          airport={airport}
          service={activeService}
          onClose={() => setActiveService(null)}
          onOpenVenue={(v) => setActiveVenue(v)}
          onBrief={(venue) => {
            setActiveBrief({
              service: activeService.code,
              service_label: activeService.label,
              venue_name: venue?.name || null,
              venue_id:   venue?.place_id || null,
            });
          }}
        />
      )}

      {activeVenue && (
        <VenueQuickDrawer
          venue={activeVenue}
          onClose={() => setActiveVenue(null)}
          onRequest={() => {
            setActiveBrief({
              service: activeService?.code || "other",
              service_label: activeService?.label || "Airport service",
              venue: activeVenue,
            });
            setActiveVenue(null);
          }}
        />
      )}

      {activeBrief && (
        <AirportRequestModal
          airport={airport}
          brief={activeBrief}
          onClose={() => setActiveBrief(null)}
        />
      )}
    </div>
  );
}


// ── Dashboard: airport hero + 6-tile service grid ─────────────────
function AirportDashboard({ airport, onBack, onOpenService }) {
  const photo = airport.photo_url;
  const subtitle = [airport.city_name, airport.country_name].filter(Boolean).join(" · ");
  return (
    <div data-testid="airport-dashboard" style={{ marginTop: 8 }}>
      {/* Hero */}
      <div
        data-testid="airport-dashboard-hero"
        style={{
          borderRadius: 18,
          overflow: "hidden",
          marginBottom: 28,
          position: "relative",
          background: photo
            ? `linear-gradient(180deg, rgba(33,28,21,0.10) 30%, rgba(33,28,21,0.55) 100%), url(${photo}) center/cover no-repeat`
            : `linear-gradient(135deg, ${T.line} 0%, ${T.brass}33 100%)`,
          aspectRatio: "16 / 6",
          minHeight: 220,
          color: "#fff",
          display: "flex", flexDirection: "column", justifyContent: "flex-end",
          padding: "26px 28px",
        }}
      >
        <button
          type="button"
          data-testid="airport-back"
          onClick={onBack}
          style={{
            position: "absolute", top: 18, left: 18,
            background: "rgba(252,250,244,0.94)", color: T.ink,
            border: 0, borderRadius: 999, padding: "7px 14px",
            fontSize: 12, fontWeight: 600, cursor: "pointer",
            fontFamily: "inherit",
          }}
        >← All airports</button>

        {airport.iata && (
          <div
            data-testid="airport-iata-badge"
            style={{
              position: "absolute", top: 18, right: 18,
              background: "rgba(252,250,244,0.96)",
              color: T.brass,
              padding: "6px 14px", borderRadius: 999,
              fontFamily: "ui-monospace, monospace",
              fontSize: 18, fontWeight: 700,
              letterSpacing: "0.10em",
            }}
          >{airport.iata}</div>
        )}

        <h1 className="t-serif" style={{ fontSize: 30, fontWeight: 500, margin: 0, lineHeight: 1.1, maxWidth: 760 }}>
          {airport.name}
        </h1>
        <p style={{ fontSize: 14, color: "rgba(252,250,244,0.85)", margin: "6px 0 0", maxWidth: 760 }}>
          {subtitle}
          {airport.time_zone ? <> · 🕐 {airport.time_zone.replace("_", " ")}</> : null}
          {airport.terminals?.length ? <> · {airport.terminals.length} terminals</> : null}
          {airport.rating ? <> · <span style={{ color: "#fff" }}>★ {airport.rating}</span></> : null}
        </p>
      </div>

      {/* 6-tile service grid */}
      <section data-testid="airport-services-grid">
        <div style={{ marginBottom: 14 }}>
          <span style={{
            fontFamily: "ui-monospace, monospace",
            fontSize: 11, letterSpacing: "0.22em",
            color: T.brass, fontWeight: 600, textTransform: "uppercase",
          }}>What we arrange here</span>
        </div>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 14,
        }}>
          {SERVICES.map((s) => {
            const ov = (airport.viator_overview && s.viator_key) ? airport.viator_overview[s.viator_key] : null;
            // Branded LC photos always take priority over Viator product
            // photos (Viator's product imagery is generic/inconsistent).
            const heroUrl = s.branded_photo || ov?.hero_url;
            const count   = ov?.count || 0;
            return (
              <button
                key={s.code}
                type="button"
                data-testid={`airport-service-${s.code}`}
                onClick={() => onOpenService(s)}
                style={{
                  background: heroUrl
                    ? `linear-gradient(180deg, rgba(33,28,21,0.10) 0%, rgba(33,28,21,0.78) 100%), url(${heroUrl}) center/cover no-repeat`
                    : T.card,
                  border: `1px solid ${heroUrl ? "transparent" : T.line}`,
                  borderRadius: 16,
                  padding: "20px 22px",
                  textAlign: "left",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  transition: "all 0.15s",
                  display: "flex", flexDirection: "column", gap: 8,
                  minHeight: 190,
                  color: heroUrl ? "#fff" : T.ink,
                  position: "relative",
                  justifyContent: "flex-end",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 10px 26px rgba(33,28,21,0.20)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
              >
                {count > 0 && (
                  <span
                    data-testid={`airport-service-count-${s.code}`}
                    style={{
                      position: "absolute", top: 14, right: 14,
                      background: "rgba(252,250,244,0.94)",
                      color: T.brass,
                      padding: "4px 10px", borderRadius: 999,
                      fontFamily: "ui-monospace, monospace",
                      fontSize: 10.5, fontWeight: 700,
                      letterSpacing: "0.10em", textTransform: "uppercase",
                    }}
                  >{count} bookable</span>
                )}
                {!heroUrl && (
                  <div style={{ fontSize: 26 }} aria-hidden="true">{s.fallback_icon}</div>
                )}
                <h3 className="t-serif" style={{
                  fontSize: 20, fontWeight: 500, margin: 0,
                  color: heroUrl ? "#fff" : T.ink,
                  textShadow: heroUrl ? "0 1px 8px rgba(0,0,0,0.45)" : "none",
                }}>{s.label}</h3>
                <p style={{
                  fontSize: 12.5,
                  color: heroUrl ? "rgba(255,255,255,0.85)" : T.ink2,
                  margin: 0, lineHeight: 1.5,
                  textShadow: heroUrl ? "0 1px 6px rgba(0,0,0,0.40)" : "none",
                }}>
                  {s.google
                    ? "Browse the live list at this airport."
                    : (count > 0 ? `${count} direct-book options via Viator · or have us arrange it.` : (SERVICE_BLURBS[s.code] || ""))}
                </p>
                <div style={{
                  fontFamily: "ui-monospace, monospace",
                  fontSize: 11, fontWeight: 600,
                  color: heroUrl ? "#fff" : T.brass,
                  letterSpacing: "0.16em", textTransform: "uppercase",
                  marginTop: 6,
                  textShadow: heroUrl ? "0 1px 6px rgba(0,0,0,0.40)" : "none",
                }}>{s.cta} →</div>
              </button>
            );
          })}
        </div>

        {/* Terminals chip row */}
        {airport.terminals && airport.terminals.length > 0 && (
          <div
            data-testid="airport-terminals"
            style={{ marginTop: 28, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}
          >
            <span style={{
              fontFamily: "ui-monospace, monospace",
              fontSize: 10.5, color: T.ink3,
              letterSpacing: "0.18em", textTransform: "uppercase",
              fontWeight: 600,
            }}>Terminals</span>
            {airport.terminals.map((t) => (
              <span
                key={t.place_id}
                style={{
                  background: T.card, border: `1px solid ${T.line2}`,
                  borderRadius: 999, padding: "5px 11px",
                  fontSize: 12, color: T.ink, fontWeight: 500,
                }}
              >{t.name}</span>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}


// ── Service sub-grid drawer (Lounges or Dining) ───────────────────
function ServiceSubGridDrawer({ airport, service, onClose, onOpenVenue, onBrief }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isViator = !!service.viator_mode;
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const path = isViator
          ? `viator/${service.viator_key}`
          : (service.code === "lounge" ? "lounges" : "dining");
        const r = await fetch(`${API}/api/airport/${encodeURIComponent(airport.place_id)}/${path}`);
        const j = await r.json();
        if (!cancelled) {
          // Normalise · Viator returns `products`, Google returns `cards`
          if (isViator) {
            setData({ ...j, cards: (j.products || []).map((p) => ({
              place_id:  p.product_code,
              name:      p.title,
              short_address: p.supplier,
              rating:    p.rating,
              user_ratings_total: p.review_count,
              photo_url: p.hero_url,
              book_url:  p.book_url,
              from_price: p.from_price,
              currency:   p.currency,
              duration:   p.duration,
              provider:   "viator",
            })) });
          } else {
            setData(j);
          }
        }
      } catch { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [airport.place_id, service.code, isViator, service.viator_key]);

  return (
    <div
      data-testid="airport-service-drawer"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(20,16,11,0.55)",
        display: "flex", justifyContent: "flex-end",
        zIndex: 1000,
      }}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(720px, 96vw)",
          background: T.bg, color: T.ink,
          height: "100%", overflowY: "auto",
          boxShadow: "-12px 0 36px rgba(0,0,0,0.22)",
        }}
      >
        <div style={{
          position: "sticky", top: 0, zIndex: 2,
          background: T.bg, borderBottom: `1px solid ${T.line}`,
          padding: "14px 22px",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
        }}>
          <div>
            <div style={{
              fontFamily: "ui-monospace, monospace",
              fontSize: 10, letterSpacing: "0.22em",
              color: T.brass, fontWeight: 600, textTransform: "uppercase",
            }}>{service.label}</div>
            <div className="t-serif" style={{ fontSize: 18, fontWeight: 500, color: T.ink }}>{airport.name}</div>
          </div>
          <button
            type="button"
            data-testid="airport-service-drawer-close"
            onClick={onClose}
            aria-label="Close"
            style={{ background: "transparent", border: 0, fontSize: 22, cursor: "pointer", color: T.ink2 }}
          >×</button>
        </div>

        <div style={{ padding: "20px 22px 30px" }}>
          {loading && <p style={{ fontSize: 13, color: T.ink3, fontStyle: "italic" }}>Sourcing live picks…</p>}
          {!loading && data && data.cards?.length > 0 && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 14 }}>
                {data.cards.map((c, i) => (
                  <PillarCard
                    key={c.place_id || i}
                    venue={c}
                    testidPrefix="airport"
                    onView={() => onOpenVenue(c)}
                    onBrief={() => onBrief?.(c)}
                    briefLabel="Send a brief"
                    topRightPill={c.from_price ? (
                      <span style={{
                        background: "rgba(33,28,21,0.85)", color: "#fff",
                        padding: "3px 9px", borderRadius: 999,
                        fontFamily: "ui-monospace, monospace",
                        fontSize: 10.5, fontWeight: 700,
                        letterSpacing: "0.06em",
                      }}>
                        from {c.currency} {Math.round(c.from_price)}
                      </span>
                    ) : null}
                  />
                ))}
              </div>
              <div style={{ display: "flex", justifyContent: "center", marginTop: 22 }}>
                <button
                  type="button"
                  data-testid="airport-service-drawer-brief"
                  onClick={onBrief}
                  style={{
                    background: "transparent", color: T.brass,
                    border: `1.5px solid ${T.brass}`, borderRadius: 999,
                    padding: "11px 22px", fontSize: 13.5, fontWeight: 500,
                    cursor: "pointer", fontFamily: "inherit",
                  }}
                >Or have us arrange {service.label.toLowerCase()} →</button>
              </div>
              {isViator && (
                <p style={{
                  fontFamily: "ui-monospace, monospace",
                  fontSize: 10, color: T.ink3, fontWeight: 600,
                  letterSpacing: "0.14em", textTransform: "uppercase",
                  textAlign: "center", margin: "16px 0 0",
                }}>Powered by Viator · A Tripadvisor company</p>
              )}
            </>
          )}
          {!loading && (!data || data.cards?.length === 0) && (
            <div style={{ textAlign: "center", padding: "40px 20px", color: T.ink2 }}>
              <p style={{ fontSize: 14, marginBottom: 14 }}>
                We couldn&apos;t find live {service.label.toLowerCase()} here. The Concierge® will arrange it by hand.
              </p>
              <button
                type="button"
                onClick={onBrief}
                style={{
                  background: T.ink, color: T.card, border: 0,
                  borderRadius: 999, padding: "11px 22px",
                  fontSize: 13.5, fontWeight: 600, cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >Send a brief</button>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}


// ── Mini card for a lounge / dining venue ─────────────────────────
function VenueMiniCard({ venue, onOpen }) {
  const isViator = venue.provider === "viator";
  const handleClick = () => {
    if (isViator && venue.book_url) {
      window.open(venue.book_url, "_blank", "noopener,noreferrer");
    } else {
      onOpen();
    }
  };
  return (
    <article
      data-testid="airport-venue-card"
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleClick(); } }}
      style={{
        background: T.card, border: `1px solid ${T.line}`,
        borderRadius: 12, overflow: "hidden",
        cursor: "pointer", display: "flex", flexDirection: "column",
        transition: "transform 0.15s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 10px 24px rgba(33,28,21,0.08)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
    >
      <div style={{
        aspectRatio: "4 / 3",
        background: venue.photo_url
          ? `url(${venue.photo_url}) center/cover no-repeat`
          : `linear-gradient(135deg, ${T.line} 0%, ${T.brass}22 100%)`,
        position: "relative",
      }}>
        {isViator && venue.from_price && (
          <span style={{
            position: "absolute", top: 8, right: 8,
            background: "rgba(33,28,21,0.85)", color: "#fff",
            padding: "3px 9px", borderRadius: 999,
            fontFamily: "ui-monospace, monospace",
            fontSize: 10.5, fontWeight: 700,
            letterSpacing: "0.06em",
          }}>
            from {venue.currency} {Math.round(venue.from_price)}
          </span>
        )}
      </div>
      <div style={{ padding: 12 }}>
        <h4 className="t-serif" style={{ fontSize: 14.5, fontWeight: 500, color: T.ink, margin: 0, lineHeight: 1.25 }}>
          {venue.name}
        </h4>
        {venue.short_address && (
          <p style={{ fontSize: 11, color: T.ink3, margin: "4px 0 0", lineHeight: 1.35 }}>{venue.short_address}</p>
        )}
        {venue.rating && (
          <p style={{ fontSize: 11.5, color: T.ink2, margin: "5px 0 0" }}>
            <span style={{ color: T.brass }}>★</span> {venue.rating}
            {venue.user_ratings_total ? <span style={{ color: T.ink3 }}> ({venue.user_ratings_total.toLocaleString()})</span> : null}
          </p>
        )}
        {isViator && (
          <p style={{
            fontFamily: "ui-monospace, monospace",
            fontSize: 10, color: T.brass, fontWeight: 700,
            letterSpacing: "0.16em", textTransform: "uppercase",
            margin: "8px 0 0",
          }}>Book on Viator ↗</p>
        )}
      </div>
    </article>
  );
}


// ── Quick venue drawer (small · just photo + reviews + CTAs) ──────
function VenueQuickDrawer({ venue, onClose, onRequest }) {
  const [voiceLine, setVoiceLine] = useState(null);
  // Venue detail · reviews + editorial summary · Parity Sprint Phase 3.
  // Card only carries thin fields; reviews need a Place Details fetch.
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const pid = venue?.place_id || venue?.id;
      if (!pid) return;
      try {
        const r = await fetch(`${API}/api/airport/place/${encodeURIComponent(pid)}`);
        if (!r.ok) return;
        const j = await r.json();
        if (!cancelled && j?.venue) setDetail(j.venue);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [venue?.place_id, venue?.id]);
  const reviews = detail?.reviews;
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const pid = venue?.place_id || venue?.id;
      if (!pid || !venue?.name) return;
      try {
        const params = new URLSearchParams({ name: venue.name });
        if (venue.editorial_summary) params.set("editorial", String(venue.editorial_summary).slice(0, 1500));
        if (venue.short_address) params.set("neighborhood", venue.short_address);
        if (venue.types_label || venue.category) params.set("types_label", venue.types_label || venue.category);
        const r = await fetch(`${API}/api/concierge/voice/airport/${encodeURIComponent(pid)}?${params.toString()}`);
        if (!r.ok) return;
        const j = await r.json();
        if (!cancelled && j?.voice_line) setVoiceLine(j.voice_line);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [venue?.place_id, venue?.id, venue?.name, venue?.editorial_summary, venue?.short_address, venue?.types_label, venue?.category]);

  return (
    <div
      data-testid="airport-venue-drawer"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(20,16,11,0.6)",
        display: "flex", justifyContent: "center", alignItems: "center",
        zIndex: 1100, padding: 20,
      }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{
        background: T.bg, color: T.ink,
        borderRadius: 18, width: "min(540px, 100%)", maxHeight: "90vh", overflowY: "auto",
      }}>
        {venue.photo_url && (
          <div style={{ aspectRatio: "16 / 9", background: `url(${venue.photo_url}) center/cover no-repeat` }} />
        )}
        <div style={{ padding: "22px 26px 26px" }}>
          <h3 className="t-serif" style={{ fontSize: 22, fontWeight: 500, margin: "0 0 6px", color: T.ink }}>
            {venue.name}
          </h3>
          {venue.short_address && (
            <p style={{ fontSize: 12.5, color: T.ink3, margin: 0 }}>{venue.short_address}</p>
          )}
          {venue.rating && (
            <p style={{ fontSize: 13, color: T.ink2, margin: "10px 0 0" }}>
              <span style={{ color: T.brass }}>★</span> <strong style={{ color: T.ink }}>{venue.rating}</strong>
              {venue.user_ratings_total ? <span style={{ color: T.ink3 }}> · {venue.user_ratings_total.toLocaleString()} reviews</span> : null}
            </p>
          )}
          <div style={{ marginTop: 14 }}>
            <DrawerVoiceLine text={voiceLine} T={T} testid="airport-drawer-concierge-voice" />
          </div>
          <DrawerReviewsBand reviews={reviews} limit={2} T={T} testid="airport-drawer-reviews" />
          <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 10 }}>
            {/* Founder ruling 30 Jun 2026 evening: Request via Concierge®
               ALWAYS comes first (filled primary). Reserve is the quiet
               secondary outline. Same order across every pillar. */}
            <button
              type="button"
              onClick={onRequest}
              data-testid="airport-venue-request"
              style={{
                background: T.ink, color: T.card,
                border: 0, borderRadius: 999,
                padding: "13px 22px", fontSize: 13.5, fontWeight: 600,
                cursor: "pointer", fontFamily: "inherit",
              }}
            >Request via Concierge®</button>
            {venue.google_maps_url && (
              <a
                href={venue.google_maps_url}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="airport-venue-google"
                style={{
                  background: "transparent", color: T.brass,
                  borderRadius: 999, padding: "11px 22px",
                  fontSize: 13.5, fontWeight: 500,
                  textAlign: "center", textDecoration: "none",
                  fontFamily: "inherit",
                  border: `1.5px solid ${T.brass}`,
                }}
              >Reserve ↗</a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ── Brief modal · captures flight + service intent ────────────────
function AirportRequestModal({ airport, brief, onClose }) {
  const embedMember = useEmbedMember();
  const [name, setName] = useState(embedMember?.name  || "");
  const [email, setEmail] = useState(embedMember?.email || "");
  const [phone, setPhone] = useState(embedMember?.phone || "");
  const [flightNo, setFlightNo] = useState("");
  const [arrDep, setArrDep] = useState("ARR");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [preferences, setPreferences] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setError(null);
    if (!name.trim() || (!email.trim() && !phone.trim())) {
      setError("Your name plus email or phone — so the Concierge® can reply.");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/airport/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          service:      brief.service,
          airport_name: airport?.name || null,
          airport_iata: airport?.iata || null,
          airport_id:   airport?.place_id || null,
          venue_name:   brief.venue?.name || null,
          venue_id:     brief.venue?.place_id || null,
          flight_no:    flightNo.trim() || null,
          arrival_or_departure: arrDep,
          date:         date || null,
          time:         time.trim() || null,
          adults:       Number(adults) || 1,
          children:     Number(children) || 0,
          preferences:  preferences.trim() || null,
          member_name:  name.trim(),
          member_email: email.trim() || undefined,
          member_phone: phone.trim() || undefined,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `submit_failed (${r.status})`);
      setDone(data);
    } catch (e) {
      setError(e.message || "Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <Backdrop onClose={onClose}>
        <div data-testid="airport-request-success" style={modalStyle}>
          <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, letterSpacing: "0.22em", color: T.brass, fontWeight: 700 }}>BY THE CONCIERGE® · AIRPORT</div>
          <h2 className="t-serif" style={{ fontSize: 26, fontWeight: 500, margin: "10px 0 14px", color: T.ink }}>Your brief is with the Concierge®.</h2>
          <p style={{ fontSize: 14.5, color: T.ink2, lineHeight: 1.55 }}>
            We&apos;ll be in touch on <strong style={{ color: T.ink }}>{email || phone}</strong> very shortly.
          </p>
          <div data-testid="airport-request-ref" style={{ display: "inline-block", padding: "9px 16px", background: T.card, border: `1px solid ${T.brass}`, borderRadius: 999, fontFamily: "ui-monospace, monospace", fontSize: 12, color: T.brass, fontWeight: 600, marginTop: 16 }}>
            {done.display_ref || done.member_reference || "REF-PENDING"}
          </div>
          <div style={{ marginTop: 22 }}>
            <button type="button" onClick={onClose} style={{ background: T.ink, color: T.card, border: 0, borderRadius: 999, padding: "10px 22px", fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Close</button>
          </div>
        </div>
      </Backdrop>
    );
  }

  return (
    <Backdrop onClose={onClose}>
      <div data-testid="airport-request-modal" style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, letterSpacing: "0.22em", color: T.brass, fontWeight: 700 }}>
          BY THE CONCIERGE® · {brief.service_label.toUpperCase()}
        </div>
        <h2 className="t-serif" style={{ fontSize: 24, fontWeight: 500, margin: "10px 0 8px", color: T.ink }}>
          {brief.venue ? brief.venue.name : brief.service_label}
        </h2>
        {airport && (
          <p style={{ fontSize: 13.5, color: T.ink2, margin: "0 0 18px" }}>
            At <strong style={{ color: T.ink }}>{airport.name}</strong>
            {airport.iata ? <> · <code style={{ color: T.brass }}>{airport.iata}</code></> : null}
          </p>
        )}
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Input value={flightNo} onChange={setFlightNo} placeholder="Flight number (e.g. AI 131)" testid="airport-modal-flight" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <select value={arrDep} onChange={(e) => setArrDep(e.target.value)} data-testid="airport-modal-arrdep" style={inputStyle}>
              <option value="ARR">Arrival</option>
              <option value="DEP">Departure</option>
            </select>
            <Input value={date} onChange={setDate} placeholder="Date" testid="airport-modal-date" type="date" />
            <Input value={time} onChange={setTime} placeholder="Time" testid="airport-modal-time" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Input value={adults} onChange={setAdults} placeholder="Adults" testid="airport-modal-adults" type="number" />
            <Input value={children} onChange={setChildren} placeholder="Children" testid="airport-modal-children" type="number" />
          </div>
          <Input value={name} onChange={setName} placeholder="Your name" testid="airport-modal-name" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Input value={email} onChange={setEmail} placeholder="Email" testid="airport-modal-email" type="email" />
            <Input value={phone} onChange={setPhone} placeholder="Phone / WhatsApp" testid="airport-modal-phone" />
          </div>
          <textarea
            data-testid="airport-modal-preferences"
            value={preferences}
            onChange={(e) => setPreferences(e.target.value)}
            placeholder="Anything else? Number of bags, mobility needs, lounge of choice…"
            rows={3}
            style={{ ...inputStyle, resize: "vertical", minHeight: 80 }}
          />
          {error && <p data-testid="airport-modal-error" style={{ color: "#9a3535", fontSize: 13, margin: 0 }}>{error}</p>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 6 }}>
            <button type="button" onClick={onClose} style={{ background: "transparent", color: T.ink2, border: `1px solid ${T.line2}`, borderRadius: 999, padding: "10px 18px", fontSize: 13.5, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
            <button type="submit" data-testid="airport-modal-submit" disabled={busy} style={{ background: T.ink, color: T.card, border: 0, borderRadius: 999, padding: "10px 22px", fontSize: 13.5, fontWeight: 600, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.65 : 1, fontFamily: "inherit" }}>
              {busy ? "Sending…" : "Send to Concierge® →"}
            </button>
          </div>
        </form>
      </div>
    </Backdrop>
  );
}


// ── Primitives ──────────────────────────────────────────────────
function Backdrop({ children, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(33,28,21,0.55)", zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
    >
      {children}
    </div>
  );
}

const modalStyle = {
  background: T.bg, color: T.ink, borderRadius: 18, padding: "28px 28px 22px",
  width: "min(560px, 100%)", maxHeight: "90vh", overflowY: "auto",
  boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
};

const inputStyle = {
  width: "100%", padding: "11px 14px", border: `1px solid ${T.line2}`,
  borderRadius: 12, background: T.card, color: T.ink, fontSize: 14.5,
  fontFamily: "inherit", outline: "none", boxSizing: "border-box",
};

function Input({ value, onChange, placeholder, testid, type = "text" }) {
  return (
    <input
      type={type}
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={inputStyle}
    />
  );
}


// ── Top bar ──────────────────────────────────────────────────────
function AirportTopBar({ onHome }) {
  return (
    <header
      data-testid="airport-header"
      style={{
        borderBottom: `1px solid ${T.line}`,
        background: T.bg,
        position: "sticky",
        top: 0,
        zIndex: 20,
      }}
    >
      <div
        style={{
          maxWidth: 1180, margin: "0 auto",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 22px", gap: 10, flexWrap: "nowrap", minWidth: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
          <Link
            to="/"
            onClick={(e) => { if (onHome) { e.preventDefault(); onHome(); } }}
            data-testid="airport-home-button"
            style={{ textDecoration: "none", color: T.ink, display: "inline-flex", alignItems: "baseline", gap: 10, minWidth: 0 }}
          >
            <span className="t-serif" style={{ fontSize: 22, fontWeight: 500, letterSpacing: "0.02em" }}>
              /airport
            </span>
            <span
              data-testid="airport-header-by-concierge"
              className="lc-mobile-hide-on-narrow"
              style={{
                fontFamily: "ui-monospace, monospace",
                fontSize: 11,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: T.ink3,
                whiteSpace: "nowrap",
              }}
            >
              by Concierge®
            </span>
          </Link>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <ConciergeOnDutyPill context="airport" />
          <CrossSurfaceNav testidPrefix="airport-header-nav" size="compact" />
          <ConciergeSurfaceMenu testid="airport-surface-menu" />
        </div>
      </div>
    </header>
  );
}
